import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Pencil } from "lucide-react";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  Badge,
  Button,
  Card,
  CardContent,
  Separator,
} from "@/components";
import { formatCpfCnpj } from "@/core/utils";
import { PlatformsTable } from "@/pages/platforms/components/platforms-table";
import { getPlatformsByOrganization } from "@/pages/platforms/platform.api";

import * as clientsApi from "../clients.api";
import { TClientExtendedResponse } from "../clients.types";

function Row({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
        {title}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {children}
      </div>
    </div>
  );
}

const statusLabel: Record<string, string> = {
  approved: "Aprovado",
  pending_data: "Dados pendentes",
  pending_review: "Em revisão",
  rejected: "Rejeitado",
};

const statusClass: Record<string, string> = {
  approved: "bg-green-500 text-white",
  pending_data: "bg-yellow-500 text-white",
  pending_review: "bg-blue-500 text-white",
  rejected: "bg-red-500 text-white",
};

export const SubPartnerDetailPage: React.FC = () => {
  const { clientId, subId } = useParams<{ clientId: string; subId: string }>();
  const navigate = useNavigate();

  const { data: org, isLoading } = useQuery<TClientExtendedResponse>({
    queryKey: ["sub-partner", subId],
    queryFn: () => clientsApi.getClientById(subId!),
    enabled: !!subId,
    staleTime: 60_000,
  });

  const {
    data: subPartnerPlatforms = [],
    isLoading: isLoadingPlatforms,
  } = useQuery({
    queryKey: ["sub-partner-platforms", subId],
    queryFn: () => getPlatformsByOrganization(subId!),
    enabled: !!subId,
    staleTime: 60_000,
  });

  const isPJ =
    org?.document_type === "cnpj" ||
    (!!org?.cnpj && org.document_type !== "cpf");

  return (
    <div className="flex flex-col w-full h-full space-y-6 p-4 overflow-auto">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/clients/${clientId}/sub-partners`)}
            className="p-3 hover:bg-muted/50"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {isLoading ? "Carregando…" : (org?.name ?? "Parceiro")}
            </h1>
            {org?.status && (
              <Badge className={`mt-1 ${statusClass[org.status] ?? ""}`}>
                {statusLabel[org.status] ?? org.status}
              </Badge>
            )}
          </div>
        </div>

        <Button
          size="sm"
          variant="outline"
          onClick={() =>
            navigate(`/clients/${clientId}/sub-partners/${subId}/edit`)
          }
        >
          <Pencil className="h-4 w-4 mr-2" />
          Editar
        </Button>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground p-8 text-center">Carregando dados…</div>
      ) : !org ? (
        <div className="text-muted-foreground p-8 text-center">Parceiro não encontrado</div>
      ) : (
        <div className="space-y-6 max-w-4xl">
        <Card>
          <CardContent className="p-6 space-y-6">
            {/* Dados principais */}
            <Section title={isPJ ? "Dados da Empresa" : "Dados da Pessoa"}>
              <Row label="Nome" value={org.name} />
              {isPJ ? (
                <>
                  <Row label="CNPJ" value={org.cnpj ? formatCpfCnpj(org.cnpj) : undefined} />
                  <Row label="Razão Social" value={org.corporate_name} />
                </>
              ) : (
                <Row
                  label="CPF"
                  value={org.cpf ? formatCpfCnpj(org.cpf) : undefined}
                />
              )}
              <Row label="E-mail" value={org.email} />
              <Row label="Tipo" value={isPJ ? "Pessoa Jurídica" : "Pessoa Física"} />
              <Row label="Classificação" value={(org as any).business_classification} />
            </Section>

            {org.address && (
              <>
                <Separator />
                <Section title="Endereço">
                  <Row label="CEP" value={org.address.postal_code} />
                  <Row label="Rua" value={org.address.street} />
                  <Row label="Número" value={org.address.number} />
                  <Row label="Complemento" value={org.address.complement} />
                  <Row label="Bairro" value={org.address.neighborhood} />
                  <Row label="Cidade" value={org.address.city} />
                  <Row label="Estado" value={org.address.state} />
                </Section>
              </>
            )}

            {org.company_representative && (
              <>
                <Separator />
                <Section title={isPJ ? "Representante" : "Dados Pessoais"}>
                  <Row
                    label="Nome"
                    value={[
                      org.company_representative.first_name,
                      org.company_representative.last_name,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  />
                  <Row label="E-mail" value={org.company_representative.personal_email} />
                  <Row
                    label="CPF"
                    value={
                      org.company_representative.cpf
                        ? formatCpfCnpj(org.company_representative.cpf)
                        : undefined
                    }
                  />
                  <Row label="Telefone" value={org.company_representative.mobile_phone} />
                </Section>
              </>
            )}

            {org.bank_account && (
              <>
                <Separator />
                <Section title="Dados Bancários">
                  <Row label="Banco" value={org.bank_account.bank_name} />
                  <Row label="Agência" value={org.bank_account.bank_branch} />
                  <Row label="Conta" value={org.bank_account.checking_account} />
                  <Row label="Dígito" value={org.bank_account.checking_account_digit} />
                  <Row label="Chave PIX" value={org.bank_account.pix_key ?? undefined} />
                </Section>
              </>
            )}
          </CardContent>
        </Card>

        <PlatformsTable
          platforms={subPartnerPlatforms}
          isLoading={isLoadingPlatforms}
          title="Landing Pages"
          emptyMessage="Nenhuma landing page encontrada para este parceiro."
          showActions={false}
          onEditPlatform={() => undefined}
          onDeletePlatform={() => undefined}
        />
        </div>
      )}
    </div>
  );
};
